// ============================================
// NotificationSystem.js - COMPLETE NOTIFICATION ENGINE
// Save as: src/NotificationSystem.js
// ============================================

/*
This system creates notifications for:
1. ✅ Low coin balance
2. ✅ Coins received
3. ✅ Coins deducted
4. ✅ Book uploaded
5. ✅ Book claimed by reviewer
6. ✅ Review submitted
7. ✅ Review approved
8. ✅ Review rejected
9. ✅ Book promoted
10. ✅ Subscription expiring soon
11. ✅ Subscription expired
12. ✅ Weekly limit reached
13. ✅ Book approved (NEW)
14. ✅ Book rejected (NEW)
*/

import { createActivity } from './firebaseService';

// ============================================
// NOTIFICATION TYPES
// ============================================
export const NOTIFICATION_TYPES = {
  LOW_COINS: 'low_coins',
  COINS_RECEIVED: 'coins_received',
  COINS_DEDUCTED: 'coins_deducted',
  BOOK_UPLOADED: 'book_uploaded',
  BOOK_CLAIMED: 'book_claimed',
  REVIEW_SUBMITTED: 'review_submitted',
  REVIEW_APPROVED: 'review_approved',
  REVIEW_REJECTED: 'review_rejected',
  BOOK_PROMOTED: 'book_promoted',
  SUBSCRIPTION_EXPIRING: 'subscription_expiring',
  SUBSCRIPTION_EXPIRED: 'subscription_expired',
  WEEKLY_LIMIT_REACHED: 'weekly_limit_reached',
  BOOK_HIDDEN: 'book_hidden',
  PAYMENT_RECEIVED: 'payment_received',
  BOOK_APPROVED: 'book_approved',      // NEW
  BOOK_REJECTED_ADMIN: 'book_rejected_admin',  // NEW
};

// ============================================
// CREATE NOTIFICATION FUNCTION
// ============================================
export const createNotification = async (userId, type, title, message, metadata = {}) => {
  const notification = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    userId,
    type,
    title,
    message,
    read: false,
    createdAt: new Date().toISOString(),
    metadata
  };

  try {
    await createActivity({
      type: 'notification',
      userId,
      data: notification,
      timestamp: new Date().toISOString()
    });
    
    console.log('✅ Notification created:', { userId, type, title });
    return { success: true, notification };
  } catch (error) {
    console.error('❌ Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// NOTIFICATION TEMPLATES
// ============================================

export const NotificationTemplates = {
  // Low coins warning
  lowCoins: (currentCoins) => ({
    type: NOTIFICATION_TYPES.LOW_COINS,
    title: '⚠️ Low Coin Balance',
    message: `Your coin balance is low (${currentCoins} coins). Review more books to earn coins!`,
    metadata: { currentCoins }
  }),

  // Coins received
  coinsReceived: (amount, reason, newBalance) => ({
    type: NOTIFICATION_TYPES.COINS_RECEIVED,
    title: '💰 Coins Received!',
    message: `You received ${amount} coins! ${reason}. New balance: ${newBalance}`,
    metadata: { amount, reason, newBalance }
  }),

  // Coins deducted
  coinsDeducted: (amount, reason, newBalance) => ({
    type: NOTIFICATION_TYPES.COINS_DEDUCTED,
    title: '💸 Coins Deducted',
    message: `${amount} coins deducted for ${reason}. Remaining: ${newBalance}`,
    metadata: { amount, reason, newBalance }
  }),

  // Book uploaded
  bookUploaded: (bookTitle) => ({
    type: NOTIFICATION_TYPES.BOOK_UPLOADED,
    title: '📚 Book Uploaded Successfully!',
    message: `Your book "${bookTitle}" is now live and visible to reviewers!`,
    metadata: { bookTitle }
  }),

  // Book claimed
  bookClaimed: (bookTitle, reviewerName, deadline) => ({
    type: NOTIFICATION_TYPES.BOOK_CLAIMED,
    title: '📖 Book Claimed for Review!',
    message: `${reviewerName} claimed your book "${bookTitle}". Review deadline: ${new Date(deadline).toLocaleDateString()}`,
    metadata: { bookTitle, reviewerName, deadline }
  }),

  // Review submitted
  reviewSubmitted: (bookTitle) => ({
    type: NOTIFICATION_TYPES.REVIEW_SUBMITTED,
    title: '✍️ Review Submitted!',
    message: `Your review for "${bookTitle}" has been submitted and is pending admin approval.`,
    metadata: { bookTitle }
  }),

  // Review approved
  reviewApproved: (bookTitle, coinsEarned, bonusCoins) => ({
    type: NOTIFICATION_TYPES.REVIEW_APPROVED,
    title: '✅ Review Approved!',
    message: `Your review for "${bookTitle}" was approved! You earned ${coinsEarned} coins${bonusCoins ? ` + ${bonusCoins} bonus` : ''}.`,
    metadata: { bookTitle, coinsEarned, bonusCoins }
  }),

  // Review rejected
  reviewRejected: (bookTitle, reason) => ({
    type: NOTIFICATION_TYPES.REVIEW_REJECTED,
    title: '❌ Review Needs Revision',
    message: `Your review for "${bookTitle}" was rejected. Reason: ${reason}. Please revise and resubmit.`,
    metadata: { bookTitle, reason }
  }),

  // New review received
  reviewReceived: (bookTitle, rating, reviewerName) => ({
    type: NOTIFICATION_TYPES.REVIEW_SUBMITTED,
    title: '⭐ New Review Received!',
    message: `Your book "${bookTitle}" received a ${rating}-star review from ${reviewerName}!`,
    metadata: { bookTitle, rating, reviewerName }
  }),

  // Book promoted
  bookPromoted: (bookTitle, days) => ({
    type: NOTIFICATION_TYPES.BOOK_PROMOTED,
    title: '🚀 Book Promoted!',
    message: `Your book "${bookTitle}" is now promoted and will appear at the top for ${days} days!`,
    metadata: { bookTitle, days }
  }),

  // Subscription expiring soon
  subscriptionExpiring: (planName, daysLeft) => ({
    type: NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRING,
    title: '⏰ Subscription Expiring Soon',
    message: `Your ${planName} plan expires in ${daysLeft} days. Renew to keep your benefits!`,
    metadata: { planName, daysLeft }
  }),

  // Subscription expired
  subscriptionExpired: (planName) => ({
    type: NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRED,
    title: '❌ Subscription Expired',
    message: `Your ${planName} plan has expired. Subscribe again to continue using premium features.`,
    metadata: { planName }
  }),

  // Weekly limit reached
  weeklyLimitReached: () => ({
    type: NOTIFICATION_TYPES.WEEKLY_LIMIT_REACHED,
    title: '📊 Weekly Limit Reached',
    message: 'You\'ve reached your weekly claim limit of 10 books. Limit resets next week!',
    metadata: {}
  }),

  // Book hidden (low coins)
  bookHidden: (bookTitle, coinsNeeded) => ({
    type: NOTIFICATION_TYPES.BOOK_HIDDEN,
    title: '🚫 Book Hidden',
    message: `Your book "${bookTitle}" was hidden due to low coin balance. You need ${coinsNeeded} coins to keep it visible.`,
    metadata: { bookTitle, coinsNeeded }
  }),

  // Payment received from author
  paymentReceived: (amount, bookTitle) => ({
    type: NOTIFICATION_TYPES.PAYMENT_RECEIVED,
    title: '💵 Payment Received!',
    message: `You received ${amount} coins as payment for reviewing "${bookTitle}"`,
    metadata: { amount, bookTitle }
  }),

  // NEW: Book approved by admin
  bookApproved: (bookTitle) => ({
    type: NOTIFICATION_TYPES.BOOK_APPROVED,
    title: '✅ Book Approved!',
    message: `Great news! Your book "${bookTitle}" has been approved and is now visible to reviewers!`,
    metadata: { bookTitle }
  }),

  // NEW: Book rejected by admin
  bookRejectedByAdmin: (bookTitle, reason) => ({
    type: NOTIFICATION_TYPES.BOOK_REJECTED_ADMIN,
    title: '❌ Book Not Approved',
    message: `Your book "${bookTitle}" was not approved. Reason: ${reason}. Please revise and resubmit.`,
    metadata: { bookTitle, reason }
  })
};

// ============================================
// NOTIFICATION TRIGGERS (Call these in your app)
// ============================================

export const NotificationTriggers = {
  // Trigger when coins go below threshold
  checkLowCoins: async (userId, currentCoins, threshold = 50) => {
    if (currentCoins < threshold) {
      const template = NotificationTemplates.lowCoins(currentCoins);
      await createNotification(userId, template.type, template.title, template.message, template.metadata);
    }
  },

  // Trigger when coins are received
  onCoinsReceived: async (userId, amount, reason, newBalance) => {
    const template = NotificationTemplates.coinsReceived(amount, reason, newBalance);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when coins are deducted
  onCoinsDeducted: async (userId, amount, reason, newBalance) => {
    const template = NotificationTemplates.coinsDeducted(amount, reason, newBalance);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
    
    // Also check if now low on coins
    await NotificationTriggers.checkLowCoins(userId, newBalance);
  },

  // Trigger when book is uploaded
  onBookUploaded: async (userId, bookTitle) => {
    const template = NotificationTemplates.bookUploaded(bookTitle);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when book is claimed
  onBookClaimed: async (authorId, bookTitle, reviewerName, deadline) => {
    const template = NotificationTemplates.bookClaimed(bookTitle, reviewerName, deadline);
    await createNotification(authorId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when review is submitted
  onReviewSubmitted: async (reviewerId, bookTitle) => {
    const template = NotificationTemplates.reviewSubmitted(bookTitle);
    await createNotification(reviewerId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when review is approved
  onReviewApproved: async (reviewerId, bookTitle, coinsEarned, bonusCoins = 0) => {
    const template = NotificationTemplates.reviewApproved(bookTitle, coinsEarned, bonusCoins);
    await createNotification(reviewerId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when review is rejected
  onReviewRejected: async (reviewerId, bookTitle, reason) => {
    const template = NotificationTemplates.reviewRejected(bookTitle, reason);
    await createNotification(reviewerId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when author receives a review
  onReviewReceived: async (authorId, bookTitle, rating, reviewerName) => {
    const template = NotificationTemplates.reviewReceived(bookTitle, rating, reviewerName);
    await createNotification(authorId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when book is promoted
  onBookPromoted: async (userId, bookTitle, days) => {
    const template = NotificationTemplates.bookPromoted(bookTitle, days);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when subscription is expiring
  onSubscriptionExpiring: async (userId, planName, daysLeft) => {
    const template = NotificationTemplates.subscriptionExpiring(planName, daysLeft);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when subscription expires
  onSubscriptionExpired: async (userId, planName) => {
    const template = NotificationTemplates.subscriptionExpired(planName);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when weekly limit is reached
  onWeeklyLimitReached: async (userId) => {
    const template = NotificationTemplates.weeklyLimitReached();
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when book is hidden
  onBookHidden: async (userId, bookTitle, coinsNeeded) => {
    const template = NotificationTemplates.bookHidden(bookTitle, coinsNeeded);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // Trigger when payment is received
  onPaymentReceived: async (userId, amount, bookTitle) => {
    const template = NotificationTemplates.paymentReceived(amount, bookTitle);
    await createNotification(userId, template.type, template.title, template.message, template.metadata);
  },

  // NEW: Trigger when book is approved by admin
  onBookApproved: async (authorId, bookTitle) => {
    const template = NotificationTemplates.bookApproved(bookTitle);
    await createNotification(authorId, template.type, template.title, template.message, template.metadata);
  },

  // NEW: Trigger when book is rejected by admin
  onBookRejected: async (authorId, bookTitle, reason) => {
    const template = NotificationTemplates.bookRejectedByAdmin(bookTitle, reason);
    await createNotification(authorId, template.type, template.title, template.message, template.metadata);
  }
};

// ============================================
// NOTIFICATION ICON HELPER
// ============================================
export const getNotificationIcon = (type) => {
  const icons = {
    [NOTIFICATION_TYPES.LOW_COINS]: '⚠️',
    [NOTIFICATION_TYPES.COINS_RECEIVED]: '💰',
    [NOTIFICATION_TYPES.COINS_DEDUCTED]: '💸',
    [NOTIFICATION_TYPES.BOOK_UPLOADED]: '📚',
    [NOTIFICATION_TYPES.BOOK_CLAIMED]: '📖',
    [NOTIFICATION_TYPES.REVIEW_SUBMITTED]: '✍️',
    [NOTIFICATION_TYPES.REVIEW_APPROVED]: '✅',
    [NOTIFICATION_TYPES.REVIEW_REJECTED]: '❌',
    [NOTIFICATION_TYPES.BOOK_PROMOTED]: '🚀',
    [NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRING]: '⏰',
    [NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRED]: '❌',
    [NOTIFICATION_TYPES.WEEKLY_LIMIT_REACHED]: '📊',
    [NOTIFICATION_TYPES.BOOK_HIDDEN]: '🚫',
    [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: '💵',
    [NOTIFICATION_TYPES.BOOK_APPROVED]: '✅',           // NEW
    [NOTIFICATION_TYPES.BOOK_REJECTED_ADMIN]: '❌',    // NEW
  };
  
  return icons[type] || '🔔';
};

// ============================================
// NOTIFICATION COLOR HELPER
// ============================================
export const getNotificationColor = (type) => {
  const colors = {
    [NOTIFICATION_TYPES.LOW_COINS]: 'bg-red-100 text-red-800 border-red-300',
    [NOTIFICATION_TYPES.COINS_RECEIVED]: 'bg-green-100 text-green-800 border-green-300',
    [NOTIFICATION_TYPES.COINS_DEDUCTED]: 'bg-orange-100 text-orange-800 border-orange-300',
    [NOTIFICATION_TYPES.BOOK_UPLOADED]: 'bg-blue-100 text-blue-800 border-blue-300',
    [NOTIFICATION_TYPES.BOOK_CLAIMED]: 'bg-purple-100 text-purple-800 border-purple-300',
    [NOTIFICATION_TYPES.REVIEW_SUBMITTED]: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    [NOTIFICATION_TYPES.REVIEW_APPROVED]: 'bg-green-100 text-green-800 border-green-300',
    [NOTIFICATION_TYPES.REVIEW_REJECTED]: 'bg-red-100 text-red-800 border-red-300',
    [NOTIFICATION_TYPES.BOOK_PROMOTED]: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    [NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRING]: 'bg-orange-100 text-orange-800 border-orange-300',
    [NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRED]: 'bg-red-100 text-red-800 border-red-300',
    [NOTIFICATION_TYPES.WEEKLY_LIMIT_REACHED]: 'bg-gray-100 text-gray-800 border-gray-300',
    [NOTIFICATION_TYPES.BOOK_HIDDEN]: 'bg-red-100 text-red-800 border-red-300',
    [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: 'bg-green-100 text-green-800 border-green-300',
    [NOTIFICATION_TYPES.BOOK_APPROVED]: 'bg-green-100 text-green-800 border-green-300',        // NEW
    [NOTIFICATION_TYPES.BOOK_REJECTED_ADMIN]: 'bg-red-100 text-red-800 border-red-300',       // NEW
  };
  
  return colors[type] || 'bg-gray-100 text-gray-800 border-gray-300';
};
